import React, { useState } from 'react';
import { X } from 'lucide-react';
import NumericKeypad from '../../../components/NumericKeypad';
import { useFinancial } from '../../../contexts/FinancialContext';

export function MovimientoForm({ isOpen, onClose, tipo, onConfirm }) {
  const { createTransaction, currentClosing } = useFinancial();
  const [monto, setMonto] = useState('');
  const [metodo, setMetodo] = useState('efectivo');
  const [concepto, setConcepto] = useState('');
  const [showKeypad, setShowKeypad] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (!monto || !concepto) return;
    
    try {
      setLoading(true);
      setError('');
      
      if (!currentClosing) {
        setError('No hay un cierre de caja abierto. Por favor abra la caja antes de registrar movimientos.');
        setLoading(false);
        return;
      }
      
      // Crear la transacción con los datos del movimiento
      const transaction = await createTransaction({
        type: tipo === 'ingreso' ? 'income' : 'expense',
        amount: Number(monto),
        payment_method: metodo,
        description: concepto,
        metadata: {
          concept: concepto
        }
      });
      
      // Si llegamos aquí, la transacción se registró correctamente
      onConfirm({
        tipo,
        monto: Number(monto),
        metodo,
        concepto,
        transaction
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error al registrar el movimiento:', error);
      setError('Error al registrar el movimiento. Por favor intente nuevamente.');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-xl">
        <div className="p-4 space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {tipo === 'ingreso' ? 'Ingreso de Dinero' : 'Salida de Dinero'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Error message */}
          {error && (
            <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-sm rounded-lg">
              {error}
            </div>
          )}

          {/* Form */}
          <div className="space-y-4">
            {/* Monto */}
            <div>
              <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                Monto
              </label>
              <button
                onClick={() => setShowKeypad(true)}
                className="w-full p-3 text-left bg-gray-100 dark:bg-gray-700 rounded-lg"
              >
                <span className="text-2xl font-bold text-gray-900 dark:text-white">
                  ${monto || '0'}
                </span>
              </button>
            </div>

            {/* Método */}
            <div>
              <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                Método
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setMetodo('cash')}
                  className={`p-2 rounded-lg text-sm font-medium ${
                    metodo === 'cash'
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  Efectivo
                </button>
                <button
                  onClick={() => setMetodo('transfer')}
                  className={`p-2 rounded-lg text-sm font-medium ${
                    metodo === 'transfer'
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  Transferencia
                </button>
              </div>
            </div>

            {/* Concepto */}
            <div>
              <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                Concepto
              </label>
              <input
                type="text"
                value={concepto}
                onChange={(e) => setConcepto(e.target.value)}
                placeholder="Ingrese el concepto..."
                className="w-full p-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              />
            </div>

            {/* Submit Button */}
            <button
              onClick={handleConfirm}
              disabled={!monto || !concepto || loading}
              className={`w-full py-3 rounded-lg font-medium text-white ${
                !monto || !concepto || loading
                  ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
                  : tipo === 'ingreso'
                    ? 'bg-green-500 dark:bg-green-600 hover:bg-green-600 dark:hover:bg-green-700'
                    : 'bg-red-500 dark:bg-red-600 hover:bg-red-600 dark:hover:bg-red-700'
              }`}
            >
              {loading ? 'Procesando...' : `Confirmar ${tipo === 'ingreso' ? 'Ingreso' : 'Salida'}`}
            </button>
          </div>
        </div>

        {/* Numeric Keypad */}
        {showKeypad && (
          <NumericKeypad
            onInput={(value) => setMonto(prev => prev + value)}
            onDelete={() => setMonto(prev => prev.slice(0, -1))}
            onConfirm={() => setShowKeypad(false)}
          />
        )}
      </div>
    </div>
  );
} 